import { useEffect, useRef, useState } from "react";
import { Check, Globe2 } from "lucide-react";
import { LANGS, useLang } from "../i18n";

const LanguageSwitcher = ({ variant = "desktop", className = "", onSelect }) => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (variant === "menu") {
    return (
      <div data-testid="language-menu-mobile" className="border-t border-white/10 pt-5">
        <p className="mb-3 text-[8px] font-medium uppercase tracking-[0.16em] text-white/40">
          {t("ui.language", "Language")}
        </p>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(84px, 1fr))" }}>
          {LANGS.map((l) => (
            <button
              key={l.id}
              type="button"
              data-testid={`language-option-${l.id}-menu`}
              onClick={() => {
                setLang(l.id);
                if (onSelect) onSelect();
              }}
              className={`flex min-h-[40px] items-center justify-center whitespace-nowrap rounded-xl border px-2 text-center text-xs transition-colors duration-200 ${
                lang === l.id
                  ? "border-cyan-300/40 bg-white/10 font-semibold text-white"
                  : "border-white/10 text-fog hover:border-white/20 hover:text-white"
              }`}
            >
              {l.label.split(" (")[0]}
              {lang === l.id && <span className="ml-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`relative flex flex-col items-center gap-1 ${className}`}>
      <button
        type="button"
        aria-label="Change language"
        aria-expanded={open}
        data-testid={`language-switcher-button-${variant}`}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors duration-300 hover:border-cyan-300/50"
      >
        <Globe2 className="h-5 w-5" />
      </button>
      <span className="text-[9px] font-medium uppercase tracking-[0.12em] text-white/40">
        {t("ui.language", "Language")}
      </span>
      {open && (
        <div
          data-testid={`language-switcher-menu-${variant}`}
          className="absolute end-0 top-full z-50 mt-1.5 w-48 overflow-hidden rounded-2xl border border-white/10 bg-surface shadow-[0_16px_60px_rgba(0,0,0,0.45)]"
        >
          {LANGS.map((l) => (
            <button
              key={l.id}
              type="button"
              data-testid={`language-option-${l.id}-${variant}`}
              onClick={() => {
                setLang(l.id);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors duration-200 ${
                lang === l.id ? "bg-white/10 font-semibold text-white" : "text-fog hover:bg-white/5 hover:text-white"
              }`}
            >
              {l.label}
              {lang === l.id && <Check className="h-4 w-4 text-cyan-300" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
